import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from '../../utils/objectUtils';
import { normalizeFunnelRoute } from '../../utils/funnelRouteUtils';
import { canRegisterForPush } from '../../utils/pushNotificationUtils';
import Notification from '../../models/Notification';
import { unsubscribeFromPush } from '../../services/pushSubscriptionService';
import NewNotificationToast from '../Notifications/NewNotificationToast';
import { updateNotification } from '../../redux/ducks/notifications';
import { useAuth } from '../../redux/ducks/auth';

// deferred push setup, for staff on the admin site only. It waits for the signed-in user's roles and 2s more, so push
// work never competes with paint, and lives inside the Router so it re-evaluates on SPA route changes (the funnel never
// shows the permission prompt; the next off-funnel navigation re-attempts)
const NotificationSetup = () => {
	//#region useLocation

	const location = useLocation();

	//#endregion
	//#region useDispatch

	const dispatch = useDispatch();

	//#endregion
	//#region useAuth

	const { currentAuthUserId, isRep, isStaffManager, isMarketingManager, isOwner, isAdmin, isSuperAdmin, isHelpStaff } = useAuth();

	//#endregion
	//#region useRefs

	// keyed by uid so a second user signing in on the same tab is decided for afresh; only the
	// granted-but-token-failed state stays unlatched so it retries on the next navigation
	const setupDoneForUidRef = useRef(null);
	// the foreground-message listener attaches at most once per page load
	const listenerAttachedRef = useRef(false);

	//#endregion
	//#region useMemos

	// the roles are decoded from the same token as the user id, so they are known as soon as the id is
	const isPushUser = useMemo(() => canRegisterForPush({ isRep, isStaffManager, isMarketingManager, isOwner, isAdmin, isSuperAdmin, isHelpStaff }, window.location.hostname), [isRep, isStaffManager, isMarketingManager, isOwner, isAdmin, isSuperAdmin, isHelpStaff]);

	//#endregion
	//#region useEffects

	useEffect(() => {
		// signing out ends this browser's push subscription, so the next sign-in is decided again, even as the same user
		if (isStringEmpty(currentAuthUserId)) {
			setupDoneForUidRef.current = null;
			return undefined;
		}
		if (setupDoneForUidRef.current === currentAuthUserId) { return undefined; }

		let cancelled = false;

		const timer = setTimeout(async () => {
			if (cancelled || setupDoneForUidRef.current === currentAuthUserId) { return; }

			// anyone push is not for is never asked and loads none of the fcm sdk; a subscription the site still holds
			// from before is ended
			if (!isPushUser) {
				setupDoneForUidRef.current = currentAuthUserId;
				try {
					await unsubscribeFromPush();
				} catch (err) {
					console.error('Error ending push subscriptions:', err);
				}
				return;
			}

			// never surface the browser permission prompt mid-funnel; already-granted browsers
			// still get their token wired up silently
			const currentRoute = normalizeFunnelRoute(window.location.pathname, window.location.search);
			if (currentRoute.isFunnelRoute && (typeof window.Notification === 'undefined' || window.Notification.permission !== 'granted')) {
				return;
			}

			try {
				const { default: NotificationService } = await import('../../services/NotificationService');

				// Initialize FCM only if not in an in-app browser
				const fcmInitialized = await NotificationService.initialize();
				console.log('fcmInitialized', fcmInitialized);
				if (fcmInitialized && !cancelled) {
					// Request notification permission and register this browser
					const tokenRegistered = await NotificationService.requestPermission();
					const permissionGranted = typeof window.Notification !== 'undefined' && window.Notification.permission === 'granted';
					if (tokenRegistered === true || !permissionGranted) {
						// a dismissed prompt (permission still 'default') must latch too, or every
						// route change would re-surface the browser prompt
						setupDoneForUidRef.current = currentAuthUserId;
					}

					if (!permissionGranted) {
						return;
					}

					if (listenerAttachedRef.current) {
						return;
					}
					listenerAttachedRef.current = true;

					// Set up message listener once permission is granted, independent of this
					// run's token fetch - previously-registered browsers still receive messages
					NotificationService.onMessageListener()
						.then((payload) => {
							// Handle foreground messages here
							console.log('Received foreground message:', payload);
							if (isObjectNotEmpty(payload?.data) && isStringNotEmpty(payload?.data?.notificationId)) {
								let newNotification = new Notification({ ...(payload?.data ?? {}), cta: { url: payload?.data?.url ?? null, label: payload?.data?.label ?? null } }).toDto();
								newNotification.id = payload?.data?.notificationId;
								toast(<NewNotificationToast notification={newNotification} />, { toastId: payload?.data?.notificationId });
								dispatch(updateNotification(newNotification));
								// You can show a toast notification or handle it however you want
							}
						})
						.catch((err) => console.error('Error receiving message:', err));
				}
			} catch (err) {
				console.error('Error initializing notifications:', err);
			}
		}, 2000);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [currentAuthUserId, isPushUser, location.pathname]);

	//#endregion

	return null;
};

export default NotificationSetup;
